import { useCallback, useEffect, useRef, useState } from "react";
import { createUuid } from "../../../lib/trace/context";
import type { QuoteBudgetLocalDraft, QuoteBudgetLocalPayload } from "../types/localDraft";
import {
  exportQuoteBudgetRawDrafts, loadQuoteBudgetSettledDrafts, parseQuoteBudgetLocalDraft,
  QuoteBudgetLocalDraftError, quoteBudgetLocalDraftKey, removeQuoteBudgetLocalDraft,
  removeQuoteBudgetRecovery, withQuoteBudgetDraftLock, writeQuoteBudgetLocalDraft,
  writeQuoteBudgetRecovery, writeQuoteBudgetDraftReceipt, type QuoteBudgetRawDraftRecord,
} from "./quoteBudgetLocalDraftStore";

/** 缓存仅接收业务工作态，不接收认证票据。 */
export type QuoteBudgetLocalDraftOptions = {
  userId?: string;
  budgetOrderId: string;
  baseRevision: string | null;
  serverRevision?: string;
  editable: boolean;
  /** 权限、权威快照和工作基线同步期间不判定缓存冲突。 */
  ready?: boolean;
  dirty: boolean;
  pending: boolean;
  payload: QuoteBudgetLocalPayload | null;
  /** 由页面按实际录入规则核对旧缓存，缺省时不自动判定旧缓存冗余。 */
  hasPendingPayload?: (payload: QuoteBudgetLocalPayload) => boolean;
  onRestore: (payload: QuoteBudgetLocalPayload, revision: string) => boolean;
};

type SaveAttempt = { token: string; baseRevision: string; payload: QuoteBudgetLocalPayload };
type CacheSession = {
  key: string; userId: string; budgetOrderId: string; epoch: number; revision: string;
  knownRaw: string | null; lastPayload: string; blocked: boolean;
  records: Map<string, QuoteBudgetRawDraftRecord>; archived: QuoteBudgetRawDraftRecord[]; adopted: Array<{ key: string; raw: string }>;
  ownRaw: string | null; backupFingerprint: string; suppressed: string | null;
  restoreRender: number; save: SaveAttempt | null;
  basedOnChangeId: string | null;
  saved: { revision: string; oldRevision: string; submitted: QuoteBudgetLocalPayload } | null;
  /** 已确认保存、无额外修改或显式放弃的精确原文，不豁免后来产生的新值。 */
  settled: Map<string, string>;
  receiptRecords: Map<string, string>;
  discarding: boolean;
};

/** 按语义值比较数据，不依赖对象属性插入顺序。 */
function fingerprint(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(fingerprint).join(",")}]`;
  const item = value as Record<string, unknown>;
  return `{${Object.keys(item).sort().map((key) => `${JSON.stringify(key)}:${fingerprint(item[key])}`).join(",")}}`;
}

/** 服务端保存仅确认人数与已加入明细，不确认抽屉文本。 */
function confirmedFingerprint(payload: QuoteBudgetLocalPayload): string {
  return fingerprint({ draft: payload.draft, lines: payload.lines });
}

/** 连续刷新继承已恢复快照；同一来源的两个独立分叉仍分别保留。 */
function recoveryHeads(candidates: QuoteBudgetLocalDraft[]): QuoteBudgetLocalDraft[] {
  const byId = new Map(candidates.map((item) => [item.changeId, item]));
  const checked = new Set<string>();
  for (const candidate of candidates) {
    let current: QuoteBudgetLocalDraft | undefined = candidate;
    const chain = new Set<string>();
    while (current && !checked.has(current.changeId)) {
      if (chain.has(current.changeId)) throw conflict("本机草稿继承关系异常，已保留全部备份，请导出核对。");
      chain.add(current.changeId);
      current = current.basedOnChangeId ? byId.get(current.basedOnChangeId) : undefined;
    }
    for (const changeId of chain) checked.add(changeId);
  }
  const inherits = (ancestor: QuoteBudgetLocalDraft, descendant: QuoteBudgetLocalDraft): boolean => {
    const [ancestorWriter, ancestorSequence] = ancestor.changeId.split(":");
    let current: QuoteBudgetLocalDraft | undefined = descendant;
    const visited = new Set<string>();
    while (current && !visited.has(current.changeId)) {
      visited.add(current.changeId);
      if (current.changeId === ancestor.changeId) return true;
      const [currentWriter, currentSequence] = current.changeId.split(":");
      if (currentWriter === ancestorWriter && Number.isSafeInteger(Number(ancestorSequence))
        && Number.isSafeInteger(Number(currentSequence)) && Number(currentSequence) > Number(ancestorSequence)) return true;
      current = current.basedOnChangeId ? byId.get(current.basedOnChangeId) : undefined;
    }
    return false;
  };
  return candidates.filter((item) => !candidates.some((other) => other.changeId !== item.changeId && inherits(item, other)));
}

/** 将真实版本或标签冲突与浏览器能力异常分开。 */
function conflict(message: string): QuoteBudgetLocalDraftError {
  return new QuoteBudgetLocalDraftError("conflict", message);
}

/** 编排本机缓存、恢复与并发冲突，不调用服务器保存。 */
export function useQuoteBudgetLocalDraft(options: QuoteBudgetLocalDraftOptions) {
  const latest = useRef(options);
  latest.current = options;
  const render = useRef(0);
  render.current += 1;
  const writer = useRef(createUuid());
  const sequence = useRef(0);
  const session = useRef<CacheSession | null>(null);
  const mounted = useRef(true);
  const [message, setMessage] = useState("");
  const [blocked, setBlocked] = useState(false);

  const isCurrent = useCallback((state: CacheSession) => mounted.current && session.current === state
    && latest.current.userId === state.userId && latest.current.budgetOrderId === state.budgetOrderId, []);

  const fail = useCallback((state: CacheSession, error: unknown) => {
    if (!isCurrent(state)) return;
    if (!(error instanceof QuoteBudgetLocalDraftError) || error.kind !== "storage") {
      state.blocked = true;
      setBlocked(true);
    }
    setMessage(error instanceof Error ? error.message : "本机缓存失败，请保留页面并导出草稿。");
  }, [isCurrent]);

  /** 本标签独立持久化处理凭据；存储完全不可用时仍保留本次内存处理结果并明确提示。 */
  const settle = useCallback((state: CacheSession, records: QuoteBudgetRawDraftRecord[]) => {
    const fresh = records.filter(item => state.settled.get(item.key) !== item.raw);
    if (!fresh.length) return;
    for (const item of fresh) {
      state.settled.set(item.key, item.raw);
      state.receiptRecords.set(item.key, item.raw);
    }
    try {
      writeQuoteBudgetDraftReceipt(state.userId, state.budgetOrderId, writer.current,
        [...state.receiptRecords].map(([key, raw]) => ({ key, raw })));
    } catch (error) { fail(state, error); }
  }, [fail]);

  /** 降级路径仍核对全部可见原文，不能把缓存锁缺失变成跨标签最后写入获胜。 */
  const verifyKnownRecords = useCallback((state: CacheSession, expected = state.records) => {
    const actual = exportQuoteBudgetRawDrafts(state.userId, state.budgetOrderId);
    if (actual.some(item => expected.get(item.key)?.raw !== item.raw)) {
      throw conflict("其他标签页已更新本机草稿，已保留其内容，请导出核对后重新加载。");
    }
    return actual;
  }, []);

  /** 保存成功、React尚未回填时仍使用已确认的新revision，避免过期pagehide备份。 */
  const capture = useCallback((state: CacheSession, current = latest.current, forExport = false) => {
    if (!current.payload || !current.baseRevision || current.userId !== state.userId || current.budgetOrderId !== state.budgetOrderId) return null;
    let revision = current.baseRevision;
    let dirty = current.dirty;
    if (state.saved && current.baseRevision === state.saved.oldRevision) {
      revision = state.saved.revision;
      dirty = confirmedFingerprint(current.payload) !== confirmedFingerprint(state.saved.submitted);
    }
    const serialized = fingerprint(current.payload);
    if (!forExport && state.suppressed === serialized) return null;
    if (!forExport && state.suppressed !== null) state.suppressed = null;
    return {
      revision, dirty, pending: current.pending, serialized, epoch: state.epoch,
      snapshot: {
        schemaVersion: 1 as const, userId: state.userId, budgetOrderId: state.budgetOrderId,
        baseRevision: revision, changeId: `${writer.current}:${++sequence.current}`,
        ...(state.basedOnChangeId ? { basedOnChangeId: state.basedOnChangeId } : {}),
        updatedAt: new Date().toISOString(), payload: structuredClone(current.payload),
      },
    };
  }, []);

  /** 本标签的恢复键只在内容改变时写入，storage通知不能形成备份乒乓。 */
  const backup = useCallback((state: CacheSession) => {
    if (!isCurrent(state) || state.discarding || state.restoreRender >= render.current) return;
    const value = capture(state);
    if (!value || (!value.dirty && !value.pending)) return;
    const mark = `${value.revision}:${value.serialized}`;
    if (state.backupFingerprint === mark) return;
    try {
      state.ownRaw = writeQuoteBudgetRecovery(value.snapshot, writer.current);
      state.backupFingerprint = mark;
      const key = `${state.key}:recovery:${writer.current}`;
      state.records.set(key, { key, raw: state.ownRaw });
    } catch (error) { fail(state, error); }
  }, [capture, fail, isCurrent]);

  const flush = useCallback(async () => {
    const state = session.current;
    const current = latest.current;
    if (!state || !isCurrent(state) || current.ready === false || !current.editable || state.blocked || state.save || state.discarding || state.restoreRender >= render.current) return;
    const value = capture(state, current);
    if (!value) return;
    const transitional = state.saved && value.revision === state.saved.revision && current.baseRevision === state.saved.oldRevision;
    if (!transitional && current.serverRevision !== value.revision) {
      fail(state, conflict("服务端版本已变化，本机草稿仍保留，请导出核对后重新加载。"));
      return;
    }
    try {
      await withQuoteBudgetDraftLock(state.userId, state.budgetOrderId, () => {
        if (!isCurrent(state) || state.epoch !== value.epoch || state.revision !== value.revision
          || state.blocked || state.save || !latest.current.editable) return;
        const newest = capture(state);
        if (!newest || newest.revision !== value.revision || newest.serialized !== value.serialized
          || newest.dirty !== value.dirty || newest.pending !== value.pending) return;
        if (!value.dirty && !value.pending) {
          if (state.knownRaw !== null && !removeQuoteBudgetLocalDraft(state.userId, state.budgetOrderId, state.knownRaw)) {
            throw conflict("其他标签页已更新本机草稿，未清理其内容。");
          }
          state.knownRaw = null;
          state.records.delete(state.key);
          state.lastPayload = "";
          setMessage("");
        } else if (state.lastPayload !== `${value.revision}:${value.serialized}`) {
          state.knownRaw = writeQuoteBudgetLocalDraft(value.snapshot, state.knownRaw);
          state.basedOnChangeId = value.snapshot.changeId;
          state.records.set(state.key, { key: state.key, raw: state.knownRaw });
          state.lastPayload = `${value.revision}:${value.serialized}`;
          setMessage("已缓存本机；数据库以点击保存为准");
        }
        for (const recovery of state.adopted) {
          if (removeQuoteBudgetRecovery(recovery.key, recovery.raw)) state.records.delete(recovery.key);
        }
        state.adopted = [];
        if (state.ownRaw !== null) {
          const key = `${state.key}:recovery:${writer.current}`;
          if (removeQuoteBudgetRecovery(key, state.ownRaw)) state.records.delete(key);
          state.ownRaw = null;
          state.backupFingerprint = "";
        }
      });
    } catch (error) {
      fail(state, error);
      if (!(error instanceof QuoteBudgetLocalDraftError) || error.kind !== "storage"
        || !isCurrent(state) || state.epoch !== value.epoch || state.blocked || state.save) return;
      const newest = capture(state);
      if (!newest || newest.revision !== value.revision || newest.serialized !== value.serialized
        || newest.dirty !== value.dirty || newest.pending !== value.pending) return;
      try {
        const actual = verifyKnownRecords(state);
        if (!value.dirty && !value.pending) settle(state, actual);
        else backup(state);
      } catch (verificationError) { fail(state, verificationError); }
    }
  }, [backup, capture, fail, isCurrent, settle, verifyKnownRecords]);

  /** 定稿前按旧值清理无修改缓存；等待锁时任何输入、身份或基线变化都使本次准备失效。 */
  const prepareFinalize = useCallback(async (): Promise<boolean> => {
    const current = latest.current;
    const clean = () => latest.current.editable && !latest.current.dirty && !latest.current.pending
      && Boolean(latest.current.payload) && Boolean(latest.current.baseRevision)
      && latest.current.serverRevision === latest.current.baseRevision;
    if (!clean()) return false;
    const state = session.current;
    if (!current.userId) return state === null;
    if (!state || !isCurrent(state) || state.blocked || state.save
      || state.restoreRender >= render.current || state.revision !== current.baseRevision) return false;
    // 使准备前仍在等锁的缓存任务失效，防止定稿清理后又写回旧快照。
    const epoch = ++state.epoch;
    const revision = current.baseRevision;
    const serialized = fingerprint(current.payload);
    const knownRaw = state.knownRaw;
    let permittedRaw = knownRaw;
    const expected = new Map(state.records);
    const unchanged = () => isCurrent(state) && clean() && !state.blocked && !state.save
      && state.epoch === epoch && state.revision === revision
      && state.knownRaw === permittedRaw
      && latest.current.baseRevision === revision && latest.current.serverRevision === revision
      && state.restoreRender < render.current && fingerprint(latest.current.payload) === serialized;
    const verifyRecords = () => {
      const actual = exportQuoteBudgetRawDrafts(state.userId, state.budgetOrderId);
      if ((actual.find(item => item.key === state.key)?.raw ?? null) !== state.knownRaw
        || actual.some(item => expected.get(item.key)?.raw !== item.raw)) {
        throw conflict("其他标签页已更新本机草稿，未清理其内容，请先导出核对。");
      }
      return actual;
    };
    try {
      verifyRecords();
      const ready = await withQuoteBudgetDraftLock(state.userId, state.budgetOrderId, () => {
        if (!unchanged() || state.knownRaw !== knownRaw) return false;
        // 先核对全部原文，再删除任何键；未知或已被外部替换的恢复键也不能默默放行。
        const actual = verifyRecords();
        for (const item of actual) {
          if (!removeQuoteBudgetRecovery(item.key, item.raw)) {
            throw conflict("其他标签页已更新本机草稿，未清理其内容，请先导出核对。");
          }
          state.records.delete(item.key);
          if (item.key === state.key) { state.knownRaw = null; permittedRaw = null; }
          state.adopted = state.adopted.filter(recovery => recovery.key !== item.key);
          if (item.key === `${state.key}:recovery:${writer.current}`) state.ownRaw = null;
        }
        state.lastPayload = "";
        state.backupFingerprint = "";
        setMessage("");
        return true;
      });
      if (!ready || !unchanged()) return false;
      verifyRecords();
      state.epoch += 1;
      return true;
    } catch (error) {
      fail(state, error);
      if (!(error instanceof QuoteBudgetLocalDraftError) || error.kind !== "storage" || !unchanged()) return false;
      try { verifyRecords(); }
      catch (verificationError) {
        fail(state, verificationError);
        if (!(verificationError instanceof QuoteBudgetLocalDraftError) || verificationError.kind !== "storage") return false;
      }
      // 不假装删除成功：保留原文供导出，仅豁免已核实冗余的这份缓存。
      // 定稿会固化 SKU 并改变 revision，因此后续权威回填不要求 payload 仍逐字相同。
      settle(state, [...expected.values()]);
      state.epoch += 1;
      return true;
    }
  }, [fail, isCurrent, settle]);

  useEffect(() => {
    if (session.current && (session.current.userId !== options.userId || session.current.budgetOrderId !== options.budgetOrderId)) {
      session.current.epoch += 1;
      session.current = null;
      setBlocked(false); setMessage("");
    }
    if (options.ready === false || !options.userId || !options.baseRevision || !options.payload) return;
    const key = quoteBudgetLocalDraftKey(options.userId, options.budgetOrderId);
    if (session.current?.key === key) return;
    const state: CacheSession = {
      key, userId: options.userId, budgetOrderId: options.budgetOrderId, epoch: 0,
      revision: options.baseRevision, knownRaw: null, lastPayload: "", blocked: false,
      records: new Map(), archived: [], adopted: [], ownRaw: null, backupFingerprint: "", suppressed: null,
      restoreRender: -1, save: null, saved: null,
      basedOnChangeId: null,
      settled: new Map(), receiptRecords: new Map(), discarding: false,
    };
    session.current = state;
    setBlocked(false); setMessage("");
    try {
      // 先保留所有原始值；任一候选损坏也仍然能导出完整现场。
      for (const item of exportQuoteBudgetRawDrafts(state.userId, state.budgetOrderId)) state.records.set(item.key, item);
      state.knownRaw = state.records.get(key)?.raw ?? null;
      for (const item of loadQuoteBudgetSettledDrafts(state.userId, state.budgetOrderId)) {
        if (state.records.get(item.key)?.raw === item.raw) state.settled.set(item.key, item.raw);
      }
      const active = [...state.records.values()].filter(item => state.settled.get(item.key) !== item.raw);
      const recoveries = active.filter(item => item.key !== key);
      state.adopted = recoveries;
      const candidates = active.map(item => parseQuoteBudgetLocalDraft(item.raw, state.userId, state.budgetOrderId));
      if (!candidates.length) return;
      const heads = recoveryHeads(candidates);
      if (!heads.length) throw conflict("本机草稿继承关系异常，已保留全部备份，请导出核对。");
      // 只在权威工作态就绪时识别历史冗余；不以总额相同代替完整人数、明细和未确认输入核对。
      if (options.serverRevision === options.baseRevision && !options.dirty && !options.pending
        && heads.some(item => item.baseRevision !== options.baseRevision || !options.editable)
        && heads.every(item => confirmedFingerprint(item.payload) === confirmedFingerprint(options.payload!)
          && options.hasPendingPayload?.(item.payload) === false)) {
        settle(state, active);
        return;
      }
      const writers = new Set(heads.map((item) => item.changeId.split(":")[0]));
      const payloads = new Set(heads.map((item) => `${item.baseRevision}:${fingerprint(item.payload)}`));
      if (writers.size > 1 && payloads.size > 1) throw conflict("存在多个标签页的本机草稿，已保留全部备份，请导出核对后再继续。");
      const saved = heads.sort((a, b) => {
        const [aWriter, aSequence] = a.changeId.split(":");
        const [bWriter, bSequence] = b.changeId.split(":");
        if (aWriter === bWriter && Number.isSafeInteger(Number(aSequence)) && Number.isSafeInteger(Number(bSequence))) {
          return Number(bSequence) - Number(aSequence);
        }
        return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
      })[0];
      if (!options.editable || saved.baseRevision !== options.baseRevision || options.serverRevision !== options.baseRevision) {
        throw conflict("服务端版本或编辑权限已变化，本机草稿已保留，未覆盖当前版本。");
      }
      state.restoreRender = render.current;
      if (!options.onRestore(saved.payload, saved.baseRevision)) throw conflict("当前核价状态不允许恢复，本机草稿已保留。");
      state.basedOnChangeId = saved.changeId;
      setMessage("已恢复本机草稿，尚未保存到数据库");
    } catch (error) { fail(state, error); }
  }, [options.ready, options.userId, options.budgetOrderId, options.baseRevision, options.payload, options.editable, options.serverRevision, options.onRestore, options.hasPendingPayload, options.dirty, options.pending, fail, settle]);

  useEffect(() => {
    const state = session.current;
    if (!state || !isCurrent(state) || options.ready === false || !options.baseRevision || state.save || state.discarding) return;
    const hasUnresolvedCache = [...state.records.values()].some(item => state.settled.get(item.key) !== item.raw);
    const transition = state.saved && options.baseRevision === state.saved.oldRevision;
    const mismatched = !transition && (state.revision !== options.baseRevision || options.serverRevision !== options.baseRevision);
    if (mismatched) {
      if (options.dirty || options.pending || hasUnresolvedCache) fail(state, conflict("服务端版本已变化，本机草稿已保留，请导出核对。"));
      else state.revision = options.baseRevision;
    }
    if (!options.editable && (options.dirty || options.pending || hasUnresolvedCache)) {
      fail(state, conflict("当前版本已只读，本机草稿已保留，不能恢复为可编辑。"));
    }
    if (state.saved && options.baseRevision === state.saved.revision) state.saved = null;
  }, [options.ready, options.baseRevision, options.serverRevision, options.editable, options.dirty, options.pending, fail, isCurrent]);

  useEffect(() => {
    const timer = window.setTimeout(() => void flush(), 800);
    return () => window.clearTimeout(timer);
  }, [options.payload, options.baseRevision, options.userId, options.budgetOrderId, options.dirty, options.pending, flush]);
  useEffect(() => {
    const timer = window.setInterval(() => void flush(), 5000);
    return () => window.clearInterval(timer);
  }, [flush]);
  useEffect(() => {
    mounted.current = true;
    const preserve = () => { if (session.current) backup(session.current); };
    const visibility = () => { if (document.visibilityState === "hidden") preserve(); };
    const external = (event: StorageEvent) => {
      const state = session.current;
      if (!state || !isCurrent(state)) return;
      if (event.key !== null && event.key !== state.key && !event.key.startsWith(`${state.key}:recovery:`)) return;
      // 恢复键删除只是清理，不应让另一个页面重新生成相同备份。
      if (event.key?.startsWith(`${state.key}:recovery:`) && event.newValue === null) return;
      try {
        const records = exportQuoteBudgetRawDrafts(state.userId, state.budgetOrderId);
        for (const item of records) {
          const previous = state.records.get(item.key);
          if (previous && previous.raw !== item.raw
            && !state.archived.some((entry) => entry.key === previous.key && entry.raw === previous.raw)) state.archived.push(previous);
          state.records.set(item.key, item);
        }
        if (state.blocked) return;
        const externalRecovery = event.key?.startsWith(`${state.key}:recovery:`) && !event.key.endsWith(`:${writer.current}`);
        if ((records.find((item) => item.key === state.key)?.raw ?? null) !== state.knownRaw || externalRecovery) {
          // 先冻结，再备份；通知往返时 blocked 保证每个标签最多响应一次。
          fail(state, conflict("其他标签页已更改本机草稿，已保留当前页面备份并停止覆盖，请先导出核对。"));
          backup(state);
        }
      } catch (error) { fail(state, error); }
    };
    window.addEventListener("pagehide", preserve);
    window.addEventListener("storage", external);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      preserve();
      mounted.current = false;
      window.removeEventListener("pagehide", preserve);
      window.removeEventListener("storage", external);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [backup, fail, isCurrent]);

  /** 请求发出前登记本次保存；自身响应先更新query时不误报版本冲突。 */
  const beginSave = useCallback((): string => {
    const state = session.current;
    const current = latest.current;
    if (!state || !isCurrent(state) || !current.payload || !current.baseRevision || state.blocked || state.save) return "";
    const token = `${writer.current}:save:${++sequence.current}`;
    state.epoch += 1;
    state.save = { token, baseRevision: current.baseRevision, payload: structuredClone(current.payload) };
    return token;
  }, [isCurrent]);

  /** 保存失败恢复原基线缓存；真正的外部revision变化仍作为冲突。 */
  const saveFailed = useCallback((token: string) => {
    const state = session.current;
    if (!state || !isCurrent(state) || state.save?.token !== token) return;
    state.save = null;
    state.epoch += 1;
    if (latest.current.baseRevision !== state.revision || latest.current.serverRevision !== state.revision) {
      fail(state, conflict("服务端版本已变化，本机草稿已保留，请导出核对。"));
    }
    void flush();
  }, [fail, flush, isCurrent]);

  /** 接纳同一身份保存成功的新基线，保留保存期间的新输入并立即刷新缓存。 */
  const afterSave = useCallback((revision: string, token?: string): Promise<void> => {
    const state = session.current;
    const current = latest.current;
    if (!state || !isCurrent(state) || !current.payload || !revision || (token !== undefined && (!token || state.save?.token !== token))) return Promise.resolve();
    const attempt = state.save;
    const submitted = attempt?.payload ?? structuredClone(current.payload);
    const oldRevision = attempt?.baseRevision ?? current.baseRevision ?? state.revision;
    state.save = null;
    state.epoch += 1;
    state.revision = revision;
    state.saved = { revision, oldRevision, submitted };
    // 多标签或格式冲突不会因为数据库保存成功而被隐式解除。
    if (state.blocked) return Promise.resolve();
    return flush();
  }, [flush, isCurrent]);

  /** 冻结点击时的原文，重载读取期间不清理，也不把后来产生的新值加入放弃范围。 */
  const prepareDiscard = useCallback(() => {
    const state = session.current;
    if (!state) return { commit: async (apply = () => true, active = () => true) => active() && apply(), cancel: () => {} };
    backup(state);
    state.epoch += 1; state.save = null;
    const epoch = state.epoch;
    const expected = new Map(state.records);
    const payload = fingerprint(latest.current.payload);
    state.discarding = true;
    let completed = false;
    const current = () => isCurrent(state) && state.epoch === epoch;
    const cancel = () => { if (current()) { state.discarding = false; if (!completed) state.epoch += 1; } };
    const observeNewRecords = () => {
      if (!current()) return;
      try {
        for (const item of exportQuoteBudgetRawDrafts(state.userId, state.budgetOrderId)) {
          const old = state.records.get(item.key);
          if (old && old.raw !== item.raw) state.archived.push(old);
          state.records.set(item.key, item);
        }
      } catch { /* 读取失败时保留已知原文。 */ }
    };
    const commit = async (apply = () => true, active = () => true): Promise<boolean> => {
      const valid = () => current() && active() && fingerprint(latest.current.payload) === payload;
      const accept = () => {
        if (!valid() || !apply()) return false;
        completed = true;
        state.suppressed = payload;
        state.discarding = false; state.blocked = false; state.saved = null;
        state.revision = latest.current.baseRevision ?? state.revision;
        state.ownRaw = null; state.adopted = []; state.lastPayload = ""; state.backupFingerprint = "";
        setBlocked(false);
        return true;
      };
      try {
        return await withQuoteBudgetDraftLock(state.userId, state.budgetOrderId, () => {
          if (!valid()) return false;
          verifyKnownRecords(state, expected);
          for (const item of expected.values()) removeQuoteBudgetRecovery(item.key, item.raw);
          const remaining = verifyKnownRecords(state, expected);
          if (remaining.length) throw conflict("其他标签页已更新本机草稿，已保留其内容，请导出核对后重新加载。");
          if (!accept()) return false;
          state.knownRaw = null; state.records.clear(); state.archived = [];
          setMessage("");
          return true;
        });
      } catch (error) {
        fail(state, error);
        if (!(error instanceof QuoteBudgetLocalDraftError) || error.kind !== "storage" || !valid()) {
          // 更新可见现场供下一次明确确认；本次冻结范围仍不改变。
          observeNewRecords();
          return false;
        }
        try { verifyKnownRecords(state, expected); }
        catch (verificationError) {
          fail(state, verificationError);
          if (!(verificationError instanceof QuoteBudgetLocalDraftError) || verificationError.kind !== "storage") {
            observeNewRecords();
            return false;
          }
        }
        // 无锁不删除共享草稿，只处理用户明确放弃的精确原文。
        settle(state, [...expected.values()]);
        state.knownRaw = state.records.get(state.key)?.raw ?? null;
        return accept();
      }
    };
    return { commit, cancel };
  }, [backup, fail, isCurrent, settle, verifyKnownRecords]);

  /** 直接放弃也复用冻结与条件清理流程。 */
  const discard = useCallback(async () => {
    const attempt = prepareDiscard();
    try { return await attempt.commit(); } finally { attempt.cancel(); }
  }, [prepareDiscard]);

  /** 导出当前工作及所有冲突原文，包括损坏和已观察到但被外部替换的快照。 */
  const exportBackup = useCallback(() => {
    const state = session.current;
    const current = latest.current;
    const records = state && isCurrent(state) ? [...state.archived, ...state.records.values()] : [];
    let error: string | undefined;
    if (current.userId) {
      try {
        for (const item of exportQuoteBudgetRawDrafts(current.userId, current.budgetOrderId)) {
          if (!records.some((record) => record.key === item.key && record.raw === item.raw)) records.push(item);
        }
      } catch (failure) { error = failure instanceof Error ? failure.message : "本机存储读取失败"; }
    }
    return {
      schemaVersion: 1 as const, userId: current.userId, budgetOrderId: current.budgetOrderId,
      current: state && isCurrent(state) ? capture(state, current, true)?.snapshot ?? null : null,
      records, ...(error ? { error } : {}),
    };
  }, [capture, isCurrent]);

  return { message, blocked, flush, prepareFinalize, beginSave, afterSave, saveFailed, prepareDiscard, discard, exportBackup };
}
